import { computed, ref } from 'vue'
import { getJson, setJson } from '@/modules/shared/utility/localStorage'

export type ThemeMode = 'light' | 'dark'

const STORAGE_KEY = 'streetloans.theme'

/**
 * PrimeVue is configured with `darkModeSelector: '.app-dark'`, so the whole
 * palette follows that one class on <html>. Nothing toggled it before this,
 * which is why dark mode shipped wired up but unreachable.
 */
const DARK_CLASS = 'app-dark'

const mode = ref<ThemeMode>('light')
let initialised = false

/**
 * Installed as a PWA the browser paints its own chrome — status bar, address
 * bar, task switcher card — from this colour. Left fixed, a dark console sits
 * under a bright indigo bar and stops looking like one app.
 */
const CHROME_COLOR: Record<ThemeMode, string> = {
  light: '#4f46e5',
  dark: '#111827',
}

const apply = (value: ThemeMode): void => {
  document.documentElement.classList.toggle(DARK_CLASS, value === 'dark')
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', CHROME_COLOR[value])
}

const readStored = (): ThemeMode | null => {
  const stored = getJson<string>(STORAGE_KEY)
  return stored === 'dark' || stored === 'light' ? stored : null
}

const readSystem = (): ThemeMode =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'

export const useTheme = () => {
  if (!initialised) {
    initialised = true
    // An explicit choice outranks the OS: someone who picked light on a
    // dark-themed machine meant it.
    mode.value = readStored() ?? readSystem()
    apply(mode.value)
  }

  const setMode = (value: ThemeMode): void => {
    mode.value = value
    apply(value)
    setJson(STORAGE_KEY, value)
  }

  const toggle = (): void => {
    setMode(mode.value === 'dark' ? 'light' : 'dark')
  }

  return {
    mode: computed(() => mode.value),
    isDark: computed(() => mode.value === 'dark'),
    setMode,
    toggle,
  }
}

/** Test seam: resets the module-level cache between specs. */
export const resetThemeForTests = (): void => {
  initialised = false
  mode.value = 'light'
  document.documentElement.classList.remove(DARK_CLASS)
}
